import React, { useState, useEffect } from 'react';
import { get, post, put } from '../utils/api';
import { useAuth } from '../context/AuthContext';

const STATUS_COLORS = {
  not_listed:{ bg:'#F5F5F5', color:'#aaa', label:'Not Listed' },
  draft:{ bg:'#F9F8F5', color:'#8A8A8A', label:'Draft' },
  pending_review:{ bg:'#EDF3FF', color:'#3B5BDB', label:'Pending Review' },
  live:{ bg:'#E8F5EB', color:'#2E6B3A', label:'Live' },
  needs_update:{ bg:'#FFF3DC', color:'#8B5E0A', label:'Needs Update' },
  suspended:{ bg:'#FDEAEA', color:'#8F2E24', label:'Suspended' },
  archived:{ bg:'#F5F5F5', color:'#aaa', label:'Archived' },
};

export default function BrandMarketplace() {
  const { user } = useAuth();
  const canApprove = ['super_admin','sales_manager'].includes(user?.role);
  const [data, setData] = useState({ data:[], products:[], marketplaces:[] });
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState({
    marketplace_id:'', product_name:'', listing_title:'',
    description:'', listing_url:'', status:'not_listed', notes:''
  });

  useEffect(()=>{ load(); },[]);

  const load = () => {
    setLoading(true);
    Promise.all([
      get('/bc/marketplace'),
      get('/bc/config'),
    ]).then(([d,cfg])=>{ setData(d); setConfig(cfg); }).finally(()=>setLoading(false));
  };

  const openForm = (item=null) => {
    if (item) {
      setEditing(item);
      setForm({
        marketplace_id:item.marketplace_id||'',
        product_name:item.product_name||'',
        listing_title:item.listing_title||'',
        description:item.description||'',
        listing_url:item.listing_url||'',
        status:item.status||'not_listed',
        notes:item.notes||''
      });
    } else {
      setEditing(null);
      setForm({marketplace_id:'',product_name:'',listing_title:'',
        description:'',listing_url:'',status:'not_listed',notes:''});
    }
    setShowForm(true);
  };

  const save = async () => {
    if (!form.marketplace_id || !form.product_name) {
      alert('Marketplace and product name required'); return;
    }
    setSaving(true);
    try {
      if (editing) await put('/bc/marketplace/'+editing.id, form);
      else await post('/bc/marketplace', form);
      setShowForm(false);
      load();
    } catch(err) { alert(err.message); }
    finally { setSaving(false); }
  };

  // Build cross-channel matrix
  const buildMatrix = () => {
    const products = [...new Set(data.data.map(l=>l.product_name))];
    const marketplaces = data.marketplaces;
    return { products, marketplaces };
  };

  const getListingStatus = (product, marketplaceId) => {
    const listing = data.data.find(l=>l.product_name===product && l.marketplace_id===marketplaceId);
    return listing ? STATUS_COLORS[listing.status]||STATUS_COLORS.not_listed : STATUS_COLORS.not_listed;
  };

  const { products: matrixProducts, marketplaces: matrixMarkets } = buildMatrix();

  return (
    <div className="page">
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:20}}>
        <div>
          <h2 style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.8rem',color:'#2C1F16',margin:'0 0 4px'}}>Marketplace</h2>
          <p style={{color:'#8A8A8A',fontSize:13,margin:0}}>Track product listings across all marketplaces</p>
        </div>
        <div style={{display:'flex',gap:8}}>
          <div style={{display:'flex',border:'1px solid #ddd',borderRadius:6,overflow:'hidden'}}>
            {['list','matrix'].map(v=>(
              <button key={v} onClick={()=>setView(v)}
                style={{padding:'6px 14px',border:'none',fontSize:11,cursor:'pointer',
                  background:view===v?'#556B2F':'#fff',
                  color:view===v?'#fff':'#555',fontWeight:view===v?600:400,
                  textTransform:'capitalize'}}>
                {v}
              </button>
            ))}
          </div>
          <button className="btn btn-primary btn-sm" onClick={()=>openForm()}>+ Add Listing</button>
        </div>
      </div>

      {loading ? <div className="spinner"/> : (
        <>
          {/* LIST VIEW */}
          {view==='list' && (
            data.data.length === 0 ? (
              <div className="empty-state"><h3>No listings yet</h3></div>
            ) : (
              <div style={{background:'#fff',border:'1px solid #E8E0D4',borderRadius:12,overflow:'hidden'}}>
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                  <thead>
                    <tr style={{background:'#2C1F16'}}>
                      {['Product','Marketplace','Title','Status','Last Reviewed',''].map(h=>(
                        <th key={h} style={{padding:'10px 14px',textAlign:'left',fontSize:9,
                          color:'#E8C466',textTransform:'uppercase',letterSpacing:'.08em',fontWeight:700}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.data.map((l,i)=>{
                      const st = STATUS_COLORS[l.status]||STATUS_COLORS.not_listed;
                      return (
                        <tr key={l.id} style={{background:i%2===0?'#fff':'#F9F8F5'}}>
                          <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8',fontWeight:600,color:'#2C1F16'}}>{l.product_name}</td>
                          <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8',color:'#555'}}>{l.marketplace_name}</td>
                          <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8',color:'#555'}}>{l.listing_title||'—'}</td>
                          <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8'}}>
                            <span style={{fontSize:9,padding:'3px 10px',borderRadius:20,fontWeight:600,background:st.bg,color:st.color}}>{st.label}</span>
                          </td>
                          <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8',color:'#555'}}>
                            {l.last_reviewed ? new Date(l.last_reviewed).toLocaleDateString('en-NG',{day:'numeric',month:'short'}) : '—'}
                          </td>
                          <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8'}}>
                            <button className="btn btn-ghost btn-sm" onClick={()=>openForm(l)}>Edit</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )
          )}

          {/* MATRIX VIEW */}
          {view==='matrix' && matrixProducts.length > 0 && (
            <div style={{background:'#fff',border:'1px solid #E8E0D4',borderRadius:12,overflow:'auto'}}>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                <thead>
                  <tr style={{background:'#2C1F16'}}>
                    <th style={{padding:'10px 14px',textAlign:'left',fontSize:9,
                      color:'#E8C466',textTransform:'uppercase',letterSpacing:'.08em',
                      fontWeight:700,minWidth:150}}>Product</th>
                    {matrixMarkets.map(m=>(
                      <th key={m.id} style={{padding:'10px 14px',textAlign:'center',fontSize:9,
                        color:'#E8C466',textTransform:'uppercase',letterSpacing:'.08em',fontWeight:700}}>
                        {m.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {matrixProducts.map((p,i)=>(
                    <tr key={p} style={{background:i%2===0?'#fff':'#F9F8F5'}}>
                      <td style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8',
                        fontWeight:600,color:'#2C1F16'}}>{p}</td>
                      {matrixMarkets.map(m=>{
                        const st = getListingStatus(p, m.id);
                        return (
                          <td key={m.id} style={{padding:'10px 14px',borderBottom:'1px solid #EDE7D8',textAlign:'center'}}>
                            <span style={{fontSize:9,padding:'3px 10px',borderRadius:20,
                              fontWeight:600,background:st.bg,color:st.color}}>{st.label}</span>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {showForm && config && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:520}}>
            <div className="modal-hdr">
              <span className="modal-title">{editing?'Edit Listing':'New Listing'}</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowForm(false)}>X</button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Marketplace *</label>
                  <select className="form-control" value={form.marketplace_id}
                    onChange={e=>setForm({...form,marketplace_id:e.target.value})}>
                    <option value="">Select marketplace</option>
                    {config.marketplaces.map(m=><option key={m.id} value={m.id}>{m.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Product Name *</label>
                  <input className="form-control" value={form.product_name}
                    onChange={e=>setForm({...form,product_name:e.target.value})} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Listing Title</label>
                <input className="form-control" value={form.listing_title}
                  onChange={e=>setForm({...form,listing_title:e.target.value})} />
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea className="form-control" rows={3} value={form.description}
                  onChange={e=>setForm({...form,description:e.target.value})} />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Listing URL</label>
                  <input className="form-control" value={form.listing_url}
                    onChange={e=>setForm({...form,listing_url:e.target.value})}
                    placeholder="https://..." />
                </div>
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select className="form-control" value={form.status}
                    onChange={e=>setForm({...form,status:e.target.value})}>
                    {Object.entries(STATUS_COLORS).map(([k,v])=>(
                      <option key={k} value={k}>{v.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea className="form-control" rows={2} value={form.notes}
                  onChange={e=>setForm({...form,notes:e.target.value})} />
              </div>
            </div>
            <div className="modal-ftr">
              <button className="btn btn-outline" onClick={()=>setShowForm(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>
                {saving?'Saving...':editing?'Save Changes':'Add Listing'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
